import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UserCircle, CreditCard, Envelope, ShieldCheck, ArrowRight,
  XCircle, ChartLineUp, Lock, House,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};
const money = (n) => (n > 0 ? "+" : n < 0 ? "−" : "") + "$" + Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatusBadge({ status }) {
  const map = {
    trial: { label: "Trial Active", color: "#22C55E" },
    active: { label: "Active", color: "#007AFF" },
    canceled: { label: "Canceled", color: "#FF3B30" },
    past_due: { label: "Past Due", color: "#F59E0B" },
    pending: { label: "Pending", color: "#A855F7" },
  };
  const m = map[status] || { label: "No Subscription", color: "#71717A" };
  return (
    <span
      data-testid="account-status-badge"
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-sm border font-bold"
      style={{ color: m.color, borderColor: `${m.color}66` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} /> {m.label}
    </span>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-5"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#007AFF] mb-4">
        {Icon && <Icon size={14} weight="fill" />} {title}
      </div>
      {children}
    </motion.div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, disabled, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full bg-[#0A0A0A] border border-white/15 hover:border-white/30 focus:border-[#007AFF] outline-none rounded-sm px-3 py-2.5 text-sm font-mono-d transition-colors disabled:opacity-50"
      />
    </div>
  );
}

export default function Account() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [opening, setOpening] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [ladder, setLadder] = useState(null);

  // Editable copies
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [addr1, setAddr1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postal, setPostal] = useState("");
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [prefs, setPrefs] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/account/me");
      setData(data);
      setName(data.name || "");
      setPhone(data.phone || "");
      setAddr1(data.address?.line1 || "");
      setCity(data.address?.city || "");
      setState(data.address?.state || "");
      setPostal(data.address?.postal || "");
      setPrefs(data.email_prefs || {});
      try {
        const r = await api.get("/account/stake-ladder");
        setLadder(r.data?.ladder || null);
      } catch { setLadder(null); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load account");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user === null) return;  // still loading auth
    if (user === false) { navigate("/login"); return; }
    load();
  }, [user, load, navigate]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/account/update", {
        name, phone,
        address_line1: addr1, address_city: city, address_state: state, address_postal: postal,
      });
      toast.success("Profile updated");
      await load();
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setSavingProfile(false); }
  };

  const savePwd = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) { toast.error("New password must be ≥ 6 characters"); return; }
    setSavingPwd(true);
    try {
      await api.put("/account/password", { current_password: curPwd, new_password: newPwd });
      toast.success("Password changed");
      setCurPwd(""); setNewPwd("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setSavingPwd(false); }
  };

  const togglePref = async (key) => {
    setSavingPrefs(true);
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      await api.put("/account/email-prefs", { [key]: next[key] });
      toast.success("Email preference saved");
    } catch (e) {
      toast.error("Couldn't save preference");
      setPrefs(prefs); // revert
    } finally { setSavingPrefs(false); }
  };

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/billing/portal-session");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't open Stripe portal");
      setOpening(false);
    }
  };

  const doCancel = async () => {
    if (!window.confirm("Cancel your subscription immediately? You'll keep access until the end of today, then lose pick access.")) return;
    setCanceling(true);
    try {
      await api.post("/account/cancel-subscription");
      toast.success("Subscription canceled.");
      await load();
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally { setCanceling(false); }
  };

  if (loading || !data) {
    return <div className="max-w-[1100px] mx-auto px-6 py-10 text-zinc-500 font-mono-d" data-testid="account-loading">Loading account…</div>;
  }

  const sub = data.subscription || {};
  const mp = data.my_picks || {};
  const canCancel = sub.status === "trial" || sub.status === "active";

  // Personalized P&L using the user's saved stake ladder. If no ladder is
  // saved, falls back to the default sim stake.
  const personalProfit = (() => {
    if (!ladder?.base_unit || !mp.picks?.length) return null;
    const unit = ladder.base_unit;
    let total = 0;
    for (const p of mp.picks) {
      const ml = Number(p.moneyline_used);
      if (Number.isNaN(ml)) continue;
      const stake = ml < 0 ? unit * Math.abs(ml) / 100 : unit;  // negative ml: risk more to win unit; positive ml: bet unit flat
      const winProfit = ml < 0 ? unit : (unit * ml / 100);       // win: unit on favorite, unit*ml/100 on dog
      if (p.result === "W") total += winProfit;
      else if (p.result === "L") total -= stake;
    }
    return Math.round(total);
  })();

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10" data-testid="account-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Account · Settings</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-none">MY ACCOUNT</h1>
        </div>
        <StatusBadge status={sub.status} />
      </div>
      <p className="text-sm text-zinc-400 mb-8">{data.email}</p>

      {/* Subscription summary */}
      <Section title="Subscription" icon={CreditCard} testid="account-subscription-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <div className="border border-white/10 rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Status</div>
            <div className="font-display text-2xl mt-1" data-testid="sub-status-value">
              {sub.status === "trial" ? "Free trial" : sub.status === "active" ? "Active" : sub.status === "canceled" ? "Canceled" : sub.status}
            </div>
          </div>
          {sub.status === "trial" && (
            <div className="border border-[#22C55E]/30 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#22C55E]">Trial ends</div>
              <div className="font-display text-2xl mt-1 text-[#22C55E]" data-testid="trial-end-value">{fmtDate(sub.trial_ends_at)}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">First charge: {fmtDate(sub.billing_cycle_anchor_iso)}</div>
            </div>
          )}
          {sub.status === "active" && sub.next_billing_at && (
            <div className="border border-[#007AFF]/30 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#007AFF]">Next charge</div>
              <div className="font-display text-2xl mt-1" data-testid="next-billing-value">{fmtDate(sub.next_billing_at)}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">$15 / week</div>
            </div>
          )}
          {sub.status === "canceled" && (
            <div className="border border-[#FF3B30]/30 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#FF3B30]">Canceled on</div>
              <div className="font-display text-2xl mt-1">{fmtDate(sub.canceled_at)}</div>
            </div>
          )}
          {sub.last_paid_at && (
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Last paid</div>
              <div className="font-display text-2xl mt-1">{fmtDate(sub.last_paid_at)}</div>
            </div>
          )}
        </div>

        {sub.status === "trial" && (
          <div className="bg-[#22C55E]/10 border border-[#22C55E]/40 rounded-sm px-4 py-3 mb-5 flex items-start gap-2.5" data-testid="trial-card-not-charged-notice">
            <Lock size={16} weight="fill" className="text-[#22C55E] mt-0.5 shrink-0" />
            <div className="text-sm">
              <strong className="text-[#22C55E] font-bold">Your card is NOT being charged during your trial.</strong>
              <span className="text-zinc-300"> First $15 weekly charge fires on {fmtDate(sub.billing_cycle_anchor_iso)}.</span>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2.5">
          {sub.has_stripe_customer && (
            <button
              onClick={openPortal}
              disabled={opening}
              data-testid="open-stripe-portal"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold rounded-sm hover:bg-zinc-200 transition-colors disabled:opacity-50"
            >
              <CreditCard size={14} weight="fill" /> {opening ? "Opening…" : "Manage Billing & Card"} <ArrowRight size={12} />
            </button>
          )}
          {canCancel && (
            <button
              onClick={doCancel}
              disabled={canceling}
              data-testid="cancel-subscription-btn"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 border border-[#FF3B30]/40 hover:border-[#FF3B30] text-[#FF3B30] text-xs uppercase tracking-[0.2em] font-bold rounded-sm transition-colors disabled:opacity-50"
            >
              <XCircle size={14} /> {canceling ? "Canceling…" : "Cancel subscription"}
            </button>
          )}
        </div>
      </Section>

      {/* Profile */}
      <Section title="Profile" icon={UserCircle} testid="account-profile-section">
        <form onSubmit={saveProfile} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Full Name" value={name} onChange={setName} testid="profile-name" />
            <Field label="Phone" type="tel" value={phone} onChange={setPhone} testid="profile-phone" />
          </div>
          <Field label="Email" value={data.email} onChange={() => {}} testid="profile-email" disabled />
          <div className="border-t border-white/5 pt-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3 flex items-center gap-1.5"><House size={12} /> Mailing Address</div>
            <Field label="Street Address" value={addr1} onChange={setAddr1} testid="profile-addr1" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <Field label="City" value={city} onChange={setCity} testid="profile-city" />
              <Field label="State / Region" value={state} onChange={setState} testid="profile-state" />
              <Field label="ZIP / Postal" value={postal} onChange={setPostal} testid="profile-postal" />
            </div>
          </div>
          <button
            type="submit"
            disabled={savingProfile}
            data-testid="save-profile-btn"
            className="px-5 py-2.5 bg-[#007AFF] hover:bg-[#0066D6] text-white text-xs uppercase tracking-[0.2em] font-bold rounded-sm transition-colors disabled:opacity-50"
          >
            {savingProfile ? "Saving…" : "Save changes"}
          </button>
        </form>
      </Section>

      {/* Password */}
      <Section title="Security · Change Password" icon={ShieldCheck} testid="account-password-section">
        <form onSubmit={savePwd} className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-[640px]">
          <Field label="Current password" type="password" value={curPwd} onChange={setCurPwd} testid="pwd-current" />
          <Field label="New password (≥ 6 chars)" type="password" value={newPwd} onChange={setNewPwd} testid="pwd-new" />
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={savingPwd || !curPwd || !newPwd}
              data-testid="save-pwd-btn"
              className="px-5 py-2.5 bg-[#007AFF] hover:bg-[#0066D6] text-white text-xs uppercase tracking-[0.2em] font-bold rounded-sm transition-colors disabled:opacity-50"
            >
              {savingPwd ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>
      </Section>

      {/* Email Preferences */}
      <Section title="Email Preferences" icon={Envelope} testid="account-prefs-section">
        <div className="space-y-2">
          {[
            { key: "daily_picks", label: "Daily picks blast (every morning at 9 AM ET)" },
            { key: "trial_reminders", label: "Trial reminder (day 6 of your trial)" },
            { key: "broadcasts", label: "Announcements & promotions from the team" },
          ].map((row) => (
            <label key={row.key} className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-white/[0.02] px-2 rounded-sm" data-testid={`pref-${row.key}`}>
              <input
                type="checkbox"
                checked={!!prefs[row.key]}
                disabled={savingPrefs}
                onChange={() => togglePref(row.key)}
                className="w-4 h-4 accent-[#22C55E]"
              />
              <span className="text-sm text-zinc-300">{row.label}</span>
            </label>
          ))}
        </div>
      </Section>

      {/* My picks history */}
      <Section title="My Predictions · Since You Joined" icon={ChartLineUp} testid="account-picks-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <Stat label="Picks" value={mp.bets || 0} />
          <Stat label="Record" value={`${mp.wins || 0}-${mp.losses || 0}`} />
          <Stat label="Hit Rate" value={mp.bets ? `${(mp.win_rate * 100).toFixed(1)}%` : "—"} color="#22C55E" />
          <Stat label="Net P/L (default)" value={mp.bets ? money(mp.profit) : "—"} color={mp.profit > 0 ? "#22C55E" : mp.profit < 0 ? "#FF3B30" : "#fff"} />
        </div>
        {/* Personalized P&L using user's saved stake ladder */}
        {ladder?.base_unit && mp.bets > 0 ? (
          <div className="mb-5 bg-gradient-to-r from-[#22C55E]/10 to-[#22D3EE]/5 border border-[#22C55E]/30 rounded-sm p-4" data-testid="personal-pnl-tile">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#22C55E]">If you followed every pick at your stake</div>
                <div className="font-display text-4xl mt-1" style={{ color: personalProfit > 0 ? "#22C55E" : personalProfit < 0 ? "#FF3B30" : "#fff" }} data-testid="personal-pnl-amount">
                  {money(personalProfit || 0)}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">Base unit ${ladder.base_unit} · target ${ladder.target_weekly}/wk · adjustable on <Link to="/optimizer" className="text-[#22D3EE] hover:text-white">Optimizer</Link></div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Per-pick avg</div>
                <div className="font-display text-2xl">{money(mp.bets ? (personalProfit || 0) / mp.bets : 0)}</div>
              </div>
            </div>
          </div>
        ) : !ladder?.base_unit && mp.bets > 0 ? (
          <div className="mb-5 bg-[#22D3EE]/5 border border-dashed border-[#22D3EE]/30 rounded-sm p-4 text-center" data-testid="personal-pnl-cta">
            <div className="text-sm text-zinc-300">
              See your <strong className="text-[#22D3EE]">personalized profit</strong> at YOUR bet size →{" "}
              <Link to="/optimizer" className="text-[#22D3EE] hover:text-white underline">Open the Stake Optimizer</Link>
            </div>
          </div>
        ) : null}
        {mp.picks?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
                <tr>
                  <th className="text-left py-2 pr-3">Date</th>
                  <th className="text-left py-2 pr-3">Analyst</th>
                  <th className="text-left py-2 pr-3">Pick</th>
                  <th className="text-left py-2 pr-3">vs</th>
                  <th className="text-right py-2 pr-3">ML</th>
                  <th className="text-right py-2 pr-3">Result</th>
                  <th className="text-right py-2">P/L</th>
                </tr>
              </thead>
              <tbody>
                {mp.picks.map((p, i) => (
                  <tr key={i} className="border-b border-white/5" data-testid={`mypick-row-${i}`}>
                    <td className="py-2 pr-3 text-zinc-400 font-mono-d">{p.date}</td>
                    <td className="py-2 pr-3">{p.analyst}</td>
                    <td className="py-2 pr-3 font-bold">{p.team_abbr}</td>
                    <td className="py-2 pr-3 text-zinc-500">{p.opponent_abbr}</td>
                    <td className="py-2 pr-3 text-right font-mono-d text-zinc-400">{p.moneyline_used > 0 ? `+${p.moneyline_used}` : p.moneyline_used}</td>
                    <td className="py-2 pr-3 text-right">
                      <span className={`text-[10px] uppercase tracking-widest font-bold ${p.result === "W" ? "text-[#22C55E]" : "text-[#FF3B30]"}`}>{p.result}</span>
                    </td>
                    <td className={`py-2 text-right font-mono-d ${p.profit > 0 ? "text-[#22C55E]" : p.profit < 0 ? "text-[#FF3B30]" : "text-zinc-400"}`}>
                      {money(p.profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-zinc-500 font-mono-d text-sm" data-testid="mypicks-empty">
            No settled picks yet during your membership. Stay tuned — your stats grow with every locked pick during your trial.
          </div>
        )}
        <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          History scoped to your membership windows · resumes if you reinstate
        </div>
      </Section>

      <div className="mt-10 text-center">
        <Link to="/" className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 hover:text-white" data-testid="back-to-picks">← Back to picks</Link>
      </div>
    </div>
  );
}

function Stat({ label, value, color = "#fff" }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display text-3xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
